import type { SidebarPreset } from '@/lib/navigation/sidebar-presets'
import { useCallback, useMemo, useState } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { captureRequestScope } from '@/lib/api/request-scope'
import { usersApi } from '@/lib/api/users.api'
import { createLogger } from '@/lib/logger'
import { normalizeRecordNavHref } from '@/lib/navigation/recordNavHref'
import { readSidebarPreset } from '@/lib/navigation/sidebar-presets'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'

import {
  DefaultTabPreferences,
  DocumentPreferences,
  FavoritesPreferences,
  FilterPreferences,
  InfoPanelLayoutPreferences,
  LayoutPreferences,
  ModuleTypeEnum,
  NavigationPreferences,
  PermissionActionEnum,
  PinnedNavRouteItem,
  PreferenceContextEnum,
  PreferenceScopeEnum,
  ProjectViewPreferences,
  TablePreferences,
  TaskViewMode,
  ThemePreferences,
  UserPreferenceData,
  ViewPreferences,
} from '@scrambl/shared'

import { useUserPreferencesStore } from '../store/user-preferences.store'

const logger = createLogger('hooks/useUserPreferences')

// Define display name preferences
export interface DisplayNamePreferences extends Record<string, unknown> {
  displayName: string
  autoUpdateFromName?: boolean // Whether to auto-update display name when first/last name changes
  lastUpdatedAt?: string
}

// Define the union type for possible preference data structures
export type ConcretePreferenceData =
  | TablePreferences
  | ThemePreferences
  | FilterPreferences
  | LayoutPreferences
  | DocumentPreferences
  | NavigationPreferences
  | ProjectViewPreferences
  | FavoritesPreferences
  | ViewPreferences
  | InfoPanelLayoutPreferences
  | DisplayNamePreferences
  | DefaultTabPreferences
  | Record<string, unknown> // Fallback for other/generic preferences

interface UseUserPreferencesProps {
  context: PreferenceContextEnum
  moduleType?: ModuleTypeEnum
  defaultPreferences?: ConcretePreferenceData
  readScope?: PreferenceScopeEnum
}

interface UseUserPreferencesReturn {
  preferences: ConcretePreferenceData
  rawPreferenceData?: UserPreferenceData // Expose the full UserPreferenceData if needed
  isLoading: boolean
  error: Error | null
  savePreferences: (
    newPreferenceDetails: ConcretePreferenceData,
    scope?: PreferenceScopeEnum,
  ) => Promise<UserPreferenceData | undefined>
  resetPreferences: () => Promise<void>
  fetchPreferences: () => Promise<void>
}

// Extend base return type for table context
export interface UseTablePreferencesReturn extends UseUserPreferencesReturn {
  viewMode: 'table' | 'card'
  setViewMode: (mode: 'table' | 'card') => Promise<void>
  columnWidths: Record<string, number>
  updateColumnWidth: (columnId: string, width: number) => void
}

const getDefaultTabForModule = (moduleType?: ModuleTypeEnum): string => {
  switch (moduleType) {
    case ModuleTypeEnum.ENTITY:
      return 'overview'
    case ModuleTypeEnum.PROJECT:
      return 'tasks'
    default:
      return 'overview'
  }
}

// Get default preference based on context
const getDefaultPreferenceForContext = (
  context: PreferenceContextEnum,
  moduleType?: ModuleTypeEnum,
): ConcretePreferenceData => {
  switch (context) {
    case PreferenceContextEnum.TABLE:
    case PreferenceContextEnum.TABLE_MY_TASKS:
      return {
        columnWidths: {},
        viewMode: 'card',
        groupBy: null,
      } as TablePreferences // Default to card view
    case PreferenceContextEnum.THEME:
      return { mode: 'system' } as ThemePreferences
    case PreferenceContextEnum.FILTERS:
      return { activeFilters: [] } as FilterPreferences
    case PreferenceContextEnum.LAYOUT:
      return { sidebarCollapsed: false } as LayoutPreferences
    case PreferenceContextEnum.OVERVIEW:
      return {
        version: 1,
        sections: [],
        showUnassignedFields: true,
      } as InfoPanelLayoutPreferences
    case PreferenceContextEnum.DOCUMENTS:
      return { favoriteDocuments: [] } as DocumentPreferences
    case PreferenceContextEnum.NAVIGATION:
      return {
        lastVisitedTabs: {},
        pinnedCollections: [],
        hiddenNavItems: {},
      } as NavigationPreferences
    case PreferenceContextEnum.PROJECT_VIEW:
      return {
        taskViewMode: {},
        defaultTaskViewMode: 'list',
      } as ProjectViewPreferences
    case PreferenceContextEnum.FAVORITES:
      return {
        favoriteEntities: [],
        favoriteProjects: [],
        displayOrder: 'recent',
        maxNavigationDisplay: 5,
      }
    case PreferenceContextEnum.VIEWS:
      return { views: [] } as ViewPreferences
    case PreferenceContextEnum.DEFAULT_TAB:
      return {
        defaultTab: getDefaultTabForModule(moduleType),
      } as DefaultTabPreferences
    default:
      return {}
  }
}

export function useUserPreferences({
  context,
  moduleType,
  defaultPreferences,
  readScope,
}: UseUserPreferencesProps):
  | UseUserPreferencesReturn
  | UseTablePreferencesReturn {
  // Use individual selectors to prevent unnecessary re-renders from store reference changes
  const isStoreInitialized = useUserPreferencesStore(
    state => state.isInitialized,
  )
  const storeIsLoading = useUserPreferencesStore(state => state.isLoading)
  const error = useUserPreferencesStore(state => state.error)
  const getPreference = useUserPreferencesStore(state => state.getPreference)
  const storedPreferences = useUserPreferencesStore(state => state.preferences)
  const savePreferenceToStore = useUserPreferencesStore(
    state => state.savePreference,
  )
  const resetPreferenceToDefault = useUserPreferencesStore(
    state => state.resetPreferenceToDefault,
  )
  const fetchUserPreferencesFromStore = useUserPreferencesStore(
    state => state.fetchUserPreferences,
  )

  // Wait for store initialization to prevent individual API calls. A failed
  // fetch leaves `isInitialized` false, so without the error escape every
  // surface gated on this flag waits on a load that will never arrive.
  const isLoading = storeIsLoading || (!isStoreInitialized && !error)

  const rawPreferenceData = useMemo(() => {
    // Only get preferences if store is initialized to prevent triggering fetches
    if (!isStoreInitialized) {
      return undefined
    }
    return getPreference(context, moduleType, readScope)
  }, [
    getPreference,
    context,
    moduleType,
    readScope,
    isStoreInitialized,
    storedPreferences,
  ])

  const preferences = useMemo(() => {
    if (rawPreferenceData?.preferences) {
      return rawPreferenceData.preferences as ConcretePreferenceData
    }
    return (
      defaultPreferences || getDefaultPreferenceForContext(context, moduleType)
    )
  }, [rawPreferenceData, defaultPreferences, context, moduleType])

  const savePreferences = useCallback(
    async (
      newPreferenceDetails: ConcretePreferenceData,
      scope: PreferenceScopeEnum = PreferenceScopeEnum.USER,
    ) => {
      return savePreferenceToStore(
        context,
        newPreferenceDetails,
        moduleType,
        scope,
      )
    },
    [savePreferenceToStore, context, moduleType],
  )

  const resetPreferences = useCallback(async () => {
    await resetPreferenceToDefault(context, moduleType)
  }, [resetPreferenceToDefault, context, moduleType])

  const fetchPreferences = useCallback(async () => {
    // Don't fetch if already initialized or currently loading
    if (isStoreInitialized || storeIsLoading) {
      return
    }
    await fetchUserPreferencesFromStore()
  }, [fetchUserPreferencesFromStore, isStoreInitialized, storeIsLoading])

  const isTableContext =
    context === PreferenceContextEnum.TABLE ||
    context === PreferenceContextEnum.TABLE_MY_TASKS

  // Define table-specific callbacks for all instances, but only use them for table context
  const setViewMode = useCallback(
    async (mode: 'table' | 'card') => {
      if (!isTableContext) return

      const tablePrefs = preferences as TablePreferences
      const updatedPrefs: TablePreferences = {
        ...tablePrefs,
        viewMode: mode,
      }

      await savePreferences(updatedPrefs)
    },
    [isTableContext, preferences, savePreferences],
  )

  const updateColumnWidth = useCallback(
    (columnId: string, width: number) => {
      if (!isTableContext) return

      const tablePrefs = preferences as TablePreferences
      const updatedPrefs: TablePreferences = {
        ...tablePrefs,
        columnWidths: {
          ...(tablePrefs.columnWidths || {}),
          [columnId]: width,
        },
      }

      // Save preferences immediately without affecting local state
      savePreferences(updatedPrefs).catch(error => {
        logger.error('[useUserPreferences] Error saving column width:', error)
      })
    },
    [isTableContext, preferences, savePreferences],
  )

  // Calculate columnWidths for all contexts, but only used for table context
  const columnWidths = useMemo(() => {
    if (!isTableContext) return {}

    const tablePrefs = preferences as TablePreferences
    return tablePrefs.columnWidths || {}
  }, [isTableContext, preferences])

  // Return different objects based on context
  if (isTableContext) {
    const tablePrefs = preferences as TablePreferences
    return {
      preferences: tablePrefs,
      rawPreferenceData,
      isLoading,
      error,
      savePreferences,
      resetPreferences,
      fetchPreferences,
      viewMode: tablePrefs.viewMode || 'card', // Default to card view if not specified
      setViewMode,
      columnWidths,
      updateColumnWidth,
    }
  }

  // Default return for non-table contexts
  return {
    preferences,
    rawPreferenceData,
    isLoading,
    error,
    savePreferences,
    resetPreferences,
    fetchPreferences,
  }
}

// Specialized hook for navigation preferences
export function useNavigationPreferences() {
  type PinnedCollectionItem = NonNullable<
    NavigationPreferences['pinnedCollections']
  >[number]
  // Use individual selectors to prevent unnecessary re-renders
  const storeIsLoading = useUserPreferencesStore(state => state.isLoading)
  const error = useUserPreferencesStore(state => state.error)
  const savePreferenceToStore = useUserPreferencesStore(
    state => state.savePreference,
  )
  const canManageOrg = useOrganizationStore(state => state.canManageOrg())
  const { checkPermission } = usePermissions()
  // Collection pin preferences are stored at org scope (visible to everyone),
  // but any member with MANAGE on CUSTOM_COLLECTION can change them — the
  // backend already allows this (see user-preferences.controller.ts
  // `canManageOrganizationPreferences`, which accepts a moduleType-scoped
  // MANAGE permission). Mirror that on the frontend so collection managers
  // can actually reach the pin controls instead of seeing a blocked UI.
  const canManageCollections = checkPermission(
    ModuleTypeEnum.CUSTOM_COLLECTION,
    PermissionActionEnum.MANAGE,
  )
  const canUpdateOrgNavigation = canManageOrg || canManageCollections

  // Read each navigation preference through a direct store selector instead of
  // a useMemo. The previous memo form listed `storedPreferences` as a trigger
  // dependency but never referenced it in the body (the value is read via
  // getPreference's internal get()). React Compiler derives dependencies from
  // what the body actually uses, so it ignored that trigger and cached the
  // result permanently — leaving the sidebar stale until a full reload. A
  // selector is a real subscription: getPreference returns a stable object
  // reference, so it only triggers a re-render when the resolved preference
  // actually changes (e.g. after a save).
  const rawUserPreferenceData = useUserPreferencesStore(state =>
    state.isInitialized
      ? state.getPreference(
          PreferenceContextEnum.NAVIGATION,
          undefined,
          PreferenceScopeEnum.USER,
        )
      : undefined,
  )

  const rawLegacyOrgPreferenceData = useUserPreferencesStore(state =>
    state.isInitialized
      ? state.getPreference(
          PreferenceContextEnum.NAVIGATION,
          undefined,
          PreferenceScopeEnum.ORGANIZATION,
        )
      : undefined,
  )

  const rawCollectionOrgPreferenceData = useUserPreferencesStore(state =>
    state.isInitialized
      ? state.getPreference(
          PreferenceContextEnum.NAVIGATION,
          ModuleTypeEnum.CUSTOM_COLLECTION,
          PreferenceScopeEnum.ORGANIZATION,
        )
      : undefined,
  )

  const rawOrgPreferenceData =
    rawCollectionOrgPreferenceData ?? rawLegacyOrgPreferenceData

  const preferences = useMemo(() => {
    const userPrefs = rawUserPreferenceData?.preferences as
      | NavigationPreferences
      | undefined
    const orgPrefs = rawOrgPreferenceData?.preferences as
      | NavigationPreferences
      | undefined
    return {
      lastVisitedTabs: userPrefs?.lastVisitedTabs || {},
      lastVisitedPaths: userPrefs?.lastVisitedPaths,
      pinnedCollections: orgPrefs?.pinnedCollections || [],
      // Per-user nav visibility overrides live on the USER-scoped record so
      // each member tailors their own sidebar (scoped to the active org).
      hiddenNavItems: userPrefs?.hiddenNavItems || {},
      sidebarPreset: readSidebarPreset(userPrefs?.sidebarPreset),
      // Pinned module/page routes are personal, like hiddenNavItems.
      pinnedNavItems: userPrefs?.pinnedNavItems || [],
    } as NavigationPreferences
  }, [rawOrgPreferenceData, rawUserPreferenceData])

  const updatePreference = useUserPreferencesStore(
    state => state.updatePreference,
  )
  const isInitialized = useUserPreferencesStore(state => state.isInitialized)
  const updateUserNavigation = useCallback(
    (update: (current: NavigationPreferences) => NavigationPreferences) => {
      const request = captureRequestScope()
      return updatePreference(PreferenceContextEnum.NAVIGATION, current => {
        request.assertCurrent()
        return update(
          (current as NavigationPreferences | undefined) ?? {
            lastVisitedTabs: {},
          },
        )
      }).then(result => {
        request.assertCurrent()
        return result
      })
    },
    [updatePreference],
  )

  const setSidebarPreset = useCallback(
    (sidebarPreset: SidebarPreset) =>
      updateUserNavigation(current => ({ ...current, sidebarPreset })),
    [updateUserNavigation],
  )

  const pinNavItem = useCallback(
    (item: PinnedNavRouteItem) => {
      const href = normalizeRecordNavHref(item.href)
      return updateUserNavigation(current => ({
        ...current,
        pinnedNavItems: [
          ...(current.pinnedNavItems ?? []).filter(
            existing => normalizeRecordNavHref(existing.href) !== href,
          ),
          { ...item, href },
        ],
      }))
    },
    [updateUserNavigation],
  )

  const unpinNavItem = useCallback(
    (href: string) =>
      updateUserNavigation(current => ({
        ...current,
        pinnedNavItems: (current.pinnedNavItems ?? []).filter(
          item =>
            normalizeRecordNavHref(item.href) !== normalizeRecordNavHref(href),
        ),
      })),
    [updateUserNavigation],
  )

  const isNavRoutePinned = useCallback(
    (href: string) =>
      preferences.pinnedNavItems?.some(
        item =>
          normalizeRecordNavHref(item.href) === normalizeRecordNavHref(href),
      ) ?? false,
    [preferences],
  )

  const setNavItemHidden = useCallback(
    (href: string, hidden: boolean) =>
      updateUserNavigation(current => ({
        ...current,
        hiddenNavItems: { ...current.hiddenNavItems, [href]: hidden },
      })),
    [updateUserNavigation],
  )

  const saveLastVisitedTab = useCallback(
    (modulePath: string, tabPath: string) =>
      updateUserNavigation(current => ({
        ...current,
        lastVisitedTabs: { ...current.lastVisitedTabs, [modulePath]: tabPath },
      })),
    [updateUserNavigation],
  )

  const saveLastVisitedPath = useCallback(
    (modulePath: string, path: string) =>
      updateUserNavigation(current => ({
        ...current,
        lastVisitedPaths: { ...current.lastVisitedPaths, [modulePath]: path },
      })),
    [updateUserNavigation],
  )

  const saveLastVisitedRoute = useCallback(
    (modulePath: string, tabPath: string, path: string) =>
      updateUserNavigation(current => ({
        ...current,
        lastVisitedTabs: { ...current.lastVisitedTabs, [modulePath]: tabPath },
        lastVisitedPaths: { ...current.lastVisitedPaths, [modulePath]: path },
      })),
    [updateUserNavigation],
  )

  const getLastVisitedTab = useCallback(
    (modulePath: string): string | undefined => {
      return preferences.lastVisitedTabs[modulePath]
    },
    [preferences],
  )

  const getLastVisitedPath = useCallback(
    (modulePath: string): string | undefined => {
      return preferences.lastVisitedPaths?.[modulePath]
    },
    [preferences],
  )

  const savePinnedCollections = useCallback(
    async (
      pinnedCollections: NavigationPreferences['pinnedCollections'],
      scope: PreferenceScopeEnum = PreferenceScopeEnum.ORGANIZATION,
    ) => {
      if (
        scope === PreferenceScopeEnum.ORGANIZATION &&
        !canUpdateOrgNavigation
      ) {
        throw new Error(
          'Insufficient permissions to update organization navigation settings.',
        )
      }
      if (scope === PreferenceScopeEnum.USER) {
        return updateUserNavigation(current => ({
          ...current,
          pinnedCollections,
        }))
      }
      const sourcePrefs =
        scope === PreferenceScopeEnum.ORGANIZATION
          ? (rawOrgPreferenceData?.preferences as
              | NavigationPreferences
              | undefined)
          : (rawUserPreferenceData?.preferences as
              | NavigationPreferences
              | undefined)
      const updatedPrefs: NavigationPreferences = {
        ...(sourcePrefs || { lastVisitedTabs: {} }),
        pinnedCollections,
      }

      // Pass CUSTOM_COLLECTION as the moduleType for org-scope saves so the
      // backend's module-level MANAGE check has something to verify against
      // when the caller is a collection manager (not an org admin).
      return savePreferenceToStore(
        PreferenceContextEnum.NAVIGATION,
        updatedPrefs as unknown as Record<string, unknown>,
        scope === PreferenceScopeEnum.ORGANIZATION
          ? ModuleTypeEnum.CUSTOM_COLLECTION
          : undefined,
        scope,
      )
    },
    [
      canUpdateOrgNavigation,
      rawOrgPreferenceData,
      rawUserPreferenceData,
      savePreferenceToStore,
      updateUserNavigation,
    ],
  )

  const pinCollection = useCallback(
    async (
      item: PinnedCollectionItem,
      scope: PreferenceScopeEnum = PreferenceScopeEnum.ORGANIZATION,
    ) => {
      if (
        scope === PreferenceScopeEnum.ORGANIZATION &&
        !canUpdateOrgNavigation
      ) {
        throw new Error(
          'Insufficient permissions to update organization navigation settings.',
        )
      }
      const existing =
        scope === PreferenceScopeEnum.ORGANIZATION
          ? (
              rawOrgPreferenceData?.preferences as
                | NavigationPreferences
                | undefined
            )?.pinnedCollections || []
          : (
              rawUserPreferenceData?.preferences as
                | NavigationPreferences
                | undefined
            )?.pinnedCollections || []
      const nextPinned = [
        ...existing.filter(
          existingItem => existingItem.collectionId !== item.collectionId,
        ),
        item,
      ]
      return savePinnedCollections(nextPinned, scope)
    },
    [
      canUpdateOrgNavigation,
      rawOrgPreferenceData,
      rawUserPreferenceData,
      savePinnedCollections,
    ],
  )

  const unpinCollection = useCallback(
    async (
      collectionId: string,
      scope: PreferenceScopeEnum = PreferenceScopeEnum.ORGANIZATION,
    ) => {
      if (
        scope === PreferenceScopeEnum.ORGANIZATION &&
        !canUpdateOrgNavigation
      ) {
        throw new Error(
          'Insufficient permissions to update organization navigation settings.',
        )
      }
      const existing =
        scope === PreferenceScopeEnum.ORGANIZATION
          ? (
              rawOrgPreferenceData?.preferences as
                | NavigationPreferences
                | undefined
            )?.pinnedCollections || []
          : (
              rawUserPreferenceData?.preferences as
                | NavigationPreferences
                | undefined
            )?.pinnedCollections || []
      const nextPinned = existing.filter(
        item => item.collectionId !== collectionId,
      )
      return savePinnedCollections(nextPinned, scope)
    },
    [
      canUpdateOrgNavigation,
      rawOrgPreferenceData,
      rawUserPreferenceData,
      savePinnedCollections,
    ],
  )

  const getPinnedCollection = useCallback(
    (collectionId: string) =>
      preferences.pinnedCollections?.find(
        item => item.collectionId === collectionId,
      ),
    [preferences],
  )

  const isCollectionPinned = useCallback(
    (collectionId: string) =>
      preferences.pinnedCollections?.some(
        item => item.collectionId === collectionId,
      ) ?? false,
    [preferences],
  )

  return {
    preferences,
    saveLastVisitedTab,
    saveLastVisitedPath,
    saveLastVisitedRoute,
    getLastVisitedTab,
    getLastVisitedPath,
    pinCollection,
    unpinCollection,
    getPinnedCollection,
    isCollectionPinned,
    pinNavItem,
    unpinNavItem,
    isNavRoutePinned,
    setNavItemHidden,
    setSidebarPreset,
    isInitialized,
    canManageOrg,
    // True when the user can update org-scope navigation prefs, either via
    // org admin role or via module MANAGE (CUSTOM_COLLECTION). UI should gate
    // the sidebar pin controls on this, not on canManageOrg alone.
    canUpdateOrgNavigation,
    isLoading: storeIsLoading || !isInitialized,
    error,
  }
}

// Specialized hook for project view preferences
export function useProjectViewPreferences() {
  // Use individual selectors to prevent unnecessary re-renders
  const isStoreInitialized = useUserPreferencesStore(
    state => state.isInitialized,
  )
  const storeIsLoading = useUserPreferencesStore(state => state.isLoading)
  const error = useUserPreferencesStore(state => state.error)
  const getPreference = useUserPreferencesStore(state => state.getPreference)
  const savePreferenceToStore = useUserPreferencesStore(
    state => state.savePreference,
  )

  const rawPreferenceData = useMemo(() => {
    // Only get preferences if store is initialized
    if (!isStoreInitialized) {
      return undefined
    }
    return getPreference(PreferenceContextEnum.PROJECT_VIEW)
  }, [getPreference, isStoreInitialized])

  const preferences = useMemo(() => {
    if (rawPreferenceData?.preferences) {
      return rawPreferenceData.preferences as unknown as ProjectViewPreferences
    }
    return {
      taskViewMode: {},
      defaultTaskViewMode: 'list',
    } as ProjectViewPreferences
  }, [rawPreferenceData])

  const saveTaskViewMode = useCallback(
    async (projectId: string, viewMode: TaskViewMode) => {
      const updatedPrefs: ProjectViewPreferences = {
        ...preferences,
        taskViewMode: {
          ...preferences.taskViewMode,
          [projectId]: viewMode,
        },
      }

      return savePreferenceToStore(
        PreferenceContextEnum.PROJECT_VIEW,
        updatedPrefs as unknown as Record<string, unknown>,
      )
    },
    [savePreferenceToStore, preferences],
  )

  const getTaskViewMode = useCallback(
    (projectId: string): TaskViewMode => {
      return (
        preferences.taskViewMode[projectId] ||
        preferences.defaultTaskViewMode ||
        'list'
      )
    },
    [preferences],
  )

  const setDefaultTaskViewMode = useCallback(
    async (viewMode: TaskViewMode) => {
      const updatedPrefs: ProjectViewPreferences = {
        ...preferences,
        defaultTaskViewMode: viewMode,
      }

      return savePreferenceToStore(
        PreferenceContextEnum.PROJECT_VIEW,
        updatedPrefs as unknown as Record<string, unknown>,
      )
    },
    [savePreferenceToStore, preferences],
  )

  return {
    preferences,
    saveTaskViewMode,
    getTaskViewMode,
    setDefaultTaskViewMode,
    isLoading: storeIsLoading,
    error,
  }
}

// Specialized hook for display name management
export function useDisplayName() {
  const { user, refreshUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)

  // Get the current display name from user data
  const currentDisplayName = user?.displayName || ''

  // Auto-generate display name from first name
  const generateDisplayNameFromFirstName = useCallback((firstName: string) => {
    return firstName.trim()
  }, [])

  // Auto-generate display name from full name
  const generateDisplayNameFromFullName = useCallback(
    (firstName: string, lastName: string) => {
      const first = firstName?.trim() || ''
      const last = lastName?.trim() || ''

      if (first && last) {
        return `${first} ${last}`
      } else if (first) {
        return first
      } else if (last) {
        return last
      }

      return ''
    },
    [],
  )

  // Update display name in user profile
  const updateDisplayName = useCallback(
    async (displayName: string) => {
      if (!user?.id) return false

      try {
        setIsLoading(true)
        await usersApi.updateProfileById(user.id, { displayName })
        await refreshUser()
        return true
      } catch (error) {
        logger.error('[useDisplayName] Error updating display name:', error)
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [user?.id, refreshUser],
  )

  // Smart update that considers existing display name
  const smartUpdateDisplayName = useCallback(
    async (
      firstName: string,
      lastName: string,
      options: {
        forceUpdate?: boolean // Force update even if display name exists
        useFullName?: boolean // Use "First Last" instead of just "First"
      } = {},
    ) => {
      const { forceUpdate = false, useFullName = false } = options

      // Don't auto-update if user already has a custom display name and we're not forcing
      if (currentDisplayName && !forceUpdate) {
        return currentDisplayName
      }

      let newDisplayName: string

      if (useFullName) {
        newDisplayName = generateDisplayNameFromFullName(firstName, lastName)
      } else {
        newDisplayName = generateDisplayNameFromFirstName(firstName)
      }

      if (newDisplayName && newDisplayName !== currentDisplayName) {
        const success = await updateDisplayName(newDisplayName)
        if (success) {
          return newDisplayName
        }
      }

      return currentDisplayName
    },
    [
      currentDisplayName,
      generateDisplayNameFromFirstName,
      generateDisplayNameFromFullName,
      updateDisplayName,
    ],
  )

  // Validate display name
  const validateDisplayName = useCallback((displayName: string) => {
    const trimmed = displayName.trim()

    if (!trimmed) {
      return { isValid: false, error: 'Display name is required' }
    }

    if (trimmed.length < 1) {
      return {
        isValid: false,
        error: 'Display name must be at least 1 character',
      }
    }

    if (trimmed.length > 100) {
      return {
        isValid: false,
        error: 'Display name must be less than 100 characters',
      }
    }

    return { isValid: true, error: null }
  }, [])

  return {
    // Current state
    currentDisplayName,
    hasDisplayName: !!currentDisplayName,

    // Utility functions
    generateDisplayNameFromFirstName,
    generateDisplayNameFromFullName,
    validateDisplayName,

    // Update functions
    updateDisplayName,
    smartUpdateDisplayName,

    // Loading state
    isLoading,
  }
}

// Specialized hook for default tab preferences per module
export function useDefaultTab(moduleType: ModuleTypeEnum) {
  const isStoreInitialized = useUserPreferencesStore(
    state => state.isInitialized,
  )
  const getPreference = useUserPreferencesStore(state => state.getPreference)
  const savePreferenceToStore = useUserPreferencesStore(
    state => state.savePreference,
  )
  const storedPreferences = useUserPreferencesStore(state => state.preferences)

  const rawPreferenceData = useMemo(() => {
    if (!isStoreInitialized) return undefined
    return getPreference(
      PreferenceContextEnum.DEFAULT_TAB,
      moduleType,
      PreferenceScopeEnum.USER,
    )
  }, [getPreference, isStoreInitialized, moduleType, storedPreferences])

  const defaultTab = useMemo(() => {
    const prefs = rawPreferenceData?.preferences as
      | DefaultTabPreferences
      | undefined
    return prefs?.defaultTab || getDefaultTabForModule(moduleType)
  }, [rawPreferenceData, moduleType])

  const saveDefaultTab = useCallback(
    async (tabId: string) => {
      const updatedPrefs: DefaultTabPreferences = { defaultTab: tabId }
      return savePreferenceToStore(
        PreferenceContextEnum.DEFAULT_TAB,
        updatedPrefs as unknown as Record<string, unknown>,
        moduleType,
        PreferenceScopeEnum.USER,
      )
    },
    [savePreferenceToStore, moduleType],
  )

  return {
    defaultTab,
    saveDefaultTab,
    isLoading: !isStoreInitialized,
  }
}
